import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAsync, useDebounce } from 'react-use';

import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import {
  AzureSecurityGroup,
  isWellKnownGroupName,
  useAzureSecurityGroups,
} from '../../../../hooks';
import { ScaffolderFieldError } from '../../../components';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const SEARCH_FAILED_MESSAGE =
  'Could not search Azure security groups. Check your Azure sign-in and try again.';
const SEARCH_NARROW_MESSAGE =
  'Showing the closest matches. Type more of the name to narrow them down.';

const useStyles = makeStyles()(theme => ({
  autocomplete: {
    [`& .${autocompleteClasses.tag}`]: {
      margin: theme.spacing(0.5),
      maxWidth: `calc(100% - ${theme.spacing(1)})`,
    },
  },
  chip: {
    '&&': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      borderRadius: 16,
    },
  },
}));

export interface SecurityGroupsInputProps {
  /** The selected group ids. */
  formData: string[];
  onChange: (value: string[]) => void;
  rawErrors?: string[];
  title?: string;
  description?: string;
  required?: boolean;
  /** How many chips to show before collapsing the rest behind a counter. */
  maxTags?: number;
  /** Hide directory plumbing groups (VPN, location, app assignment) from the defaults. */
  excludeWellKnownGroups?: boolean;
  id?: string;
}

export const SecurityGroupsInput = (props: SecurityGroupsInputProps) => {
  const {
    formData,
    onChange,
    rawErrors = [],
    title = '',
    description = '',
    required = false,
    maxTags = 5,
    excludeWellKnownGroups = false,
    id,
  } = props;

  const { classes, cx } = useStyles();
  const { listOwnGroups, searchGroups, resolveGroupNames } =
    useAzureSecurityGroups();

  const selected = useMemo(() => formData ?? [], [formData]);
  const hasError = rawErrors.length > 0;
  // Only flag the control as required while it is still empty, so a filled
  // field does not keep rendering the asterisk-driven invalid state.
  const isRequired = required && selected.length === 0;

  const [names, setNames] = useState<Map<string, string>>(new Map());

  const mergeNames = useCallback(
    (entries: Iterable<[string, string | undefined]>) => {
      setNames(previous => {
        let next = previous;
        for (const [groupId, name] of entries) {
          if (!name || next.get(groupId) === name) continue;
          if (next === previous) next = new Map(previous);
          next.set(groupId, name);
        }
        return next;
      });
    },
    [],
  );

  const { value: ownGroups } = useAsync(
    () => listOwnGroups().catch(() => [] as AzureSecurityGroup[]),
    [listOwnGroups],
  );

  const ownOptions = useMemo(() => ownGroups ?? [], [ownGroups]);

  useEffect(() => {
    mergeNames(ownOptions.map(group => [group.id, group.name]));
  }, [ownOptions, mergeNames]);

  const selectedKey = selected.join(',');
  const { value: selectedNames } = useAsync(
    () => resolveGroupNames(selected).catch(() => new Map<string, string>()),
    [selectedKey, resolveGroupNames],
  );

  useEffect(() => {
    if (selectedNames) mergeNames(selectedNames);
  }, [selectedNames, mergeNames]);

  const [term, setTerm] = useState('');
  const [results, setResults] = useState<AzureSecurityGroup[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);

  const searchCache = useRef(new Map<string, AzureSecurityGroup[]>());
  const requestId = useRef(0);

  const query = term.trim();
  const isSearch = query.length >= MIN_SEARCH_LENGTH;

  useDebounce(
    () => {
      const currentRequest = ++requestId.current;

      if (!isSearch) {
        setResults([]);
        setSearchFailed(false);
        setSearchLoading(false);
        return;
      }

      // Only an exact match may be served from the cache
      const key = query.toLowerCase();
      const cached = searchCache.current.get(key);
      if (cached) {
        setResults(cached);
        setSearchFailed(false);
        setSearchLoading(false);
        return;
      }

      setSearchLoading(true);
      searchGroups(query)
        .then(found => {
          searchCache.current.set(key, found);
          mergeNames(found.map(group => [group.id, group.name]));
          if (currentRequest !== requestId.current) return;
          setResults(found);
          setSearchFailed(false);
        })
        .catch(() => {
          if (currentRequest !== requestId.current) return;
          setResults([]);
          setSearchFailed(true);
        })
        .finally(() => {
          if (currentRequest === requestId.current) setSearchLoading(false);
        });
    },
    SEARCH_DEBOUNCE_MS,
    [query, isSearch, searchGroups, mergeNames],
  );

  const options = useMemo(() => {
    // Search results are filtered by the Graph client; own groups are not.
    const source = isSearch
      ? results
      : ownOptions.filter(
          group => !excludeWellKnownGroups || !isWellKnownGroupName(group.name),
        );

    return Array.from(new Set([...selected, ...source.map(group => group.id)]));
  }, [isSearch, results, ownOptions, excludeWellKnownGroups, selected]);

  const optionLabel = useCallback(
    (option: string) => names.get(option) || option,
    [names],
  );

  let helperMessage = description;
  if (searchFailed) {
    helperMessage = SEARCH_FAILED_MESSAGE;
  } else if (isSearch && results.length > 0) {
    helperMessage = SEARCH_NARROW_MESSAGE;
  }

  return (
    <FormControl fullWidth required={required}>
      <Autocomplete
        id={id}
        className={classes.autocomplete}
        multiple
        size="small"
        value={selected}
        onChange={(_, value: string[]) => onChange(value ?? [])}
        onInputChange={(_, value) => setTerm(value)}
        options={options}
        getOptionLabel={optionLabel}
        filterSelectedOptions
        limitTags={maxTags}
        loading={searchLoading}
        loadingText="Searching Azure security groups..."
        renderOption={({ key, ...optionProps }, option) => (
          <li key={key} {...optionProps}>
            {optionLabel(option)}
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            size="small"
            required={isRequired}
            variant="outlined"
            fullWidth
            error={hasError && selected.length === 0}
            helperText={
              hasError ? (
                <ScaffolderFieldError message={rawErrors.join(', ')} />
              ) : (
                <Typography
                  variant="caption"
                  component="span"
                  color={searchFailed ? 'error' : 'text.secondary'}
                >
                  {helperMessage}
                </Typography>
              )
            }
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, className, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                {...tagProps}
                key={key}
                label={optionLabel(option)}
                size="small"
                color="secondary"
                className={cx(className, classes.chip)}
              />
            );
          })
        }
      />
    </FormControl>
  );
};
